import React, { createContext, useContext, useMemo } from "react";
import { useSectionConfiguration } from "../hooks/useSectionConfiguration";
import {
  canAddListEntry,
  canRemoveListEntry,
  getActiveListFields,
  getListFieldGridSize,
  getListLimits,
  getSectionConfig,
  isFieldActive,
  isFieldMandatory,
  isSectionActive,
} from "../utils/sectionConfiguration";

const SectionConfigContext = createContext(null);

export function SectionConfigProvider({ children }) {
  const { config, loading, error, ready } = useSectionConfiguration();

  const value = useMemo(() => ({
    config,
    loading,
    error,
    ready,
    isSectionActive: (uiSectionId) => (config ? isSectionActive(config, uiSectionId) : true),
    isFieldActive: (uiSectionId, fieldName) => (config ? isFieldActive(config, uiSectionId, fieldName) : true),
    isFieldMandatory: (uiSectionId, fieldName) => (config ? isFieldMandatory(config, uiSectionId, fieldName) : false),
    getSectionConfig: (uiSectionId) => getSectionConfig(config, uiSectionId),
    getListLimits: (uiSectionId) => getListLimits(config, uiSectionId),
    canAddListEntry: (uiSectionId, count) => (config ? canAddListEntry(config, uiSectionId, count) : true),
    canRemoveListEntry: (uiSectionId, count) => (config ? canRemoveListEntry(config, uiSectionId, count) : true),
    getActiveListFields: (uiSectionId) => (config ? getActiveListFields(config, uiSectionId) : []),
    getListFieldGridSize: (uiSectionId) => {
      const count = getActiveListFields(config, uiSectionId).length;
      return getListFieldGridSize(count);
    },
  }), [config, loading, error, ready]);

  return (
    <SectionConfigContext.Provider value={value}>
      {children}
    </SectionConfigContext.Provider>
  );
}

export function useSectionConfig() {
  const ctx = useContext(SectionConfigContext);
  if (!ctx) {
    throw new Error("useSectionConfig must be used within SectionConfigProvider");
  }
  return ctx;
}

export default SectionConfigContext;
